import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { listAllFeedback, type FeedbackEntry } from '../services/feedbackService';
import { formatFeedbackTime } from '../utils/feedback';
import { SearchInput } from '../components/SearchInput';

export function FeedbackManagementPage() {
  const { setTopbar } = useAppShell();
  useEffect(() => { setTopbar('Feedback', 'UAT feedback submitted from across the app'); }, [setTopbar]);

  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    listAllFeedback()
      .then(setEntries)
      .catch(() => setError('Could not load feedback.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e =>
      e.by.name.toLowerCase().includes(q) ||
      e.note.toLowerCase().includes(q) ||
      e.page.label.toLowerCase().includes(q) ||
      e.page.path.toLowerCase().includes(q) ||
      (e.kind ?? '').toLowerCase().includes(q));
  }, [entries, query]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-8 py-4 flex items-center justify-between gap-4 border-b border-trim">
        <SearchInput value={query} onChange={setQuery} placeholder="Search feedback…" />
        <span className="text-xs text-muted whitespace-nowrap">{entries.length} total</span>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-4">
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted border-b border-trim">
              <th className="px-3 py-2.5">From</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Page</th>
              <th className="px-3 py-2.5">Note</th>
              <th className="px-3 py-2.5">Sent</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} className="border-b border-wash hover:bg-[#FAFAFA] text-sm align-top">
                <td className="px-3 py-2.5 font-medium text-ink whitespace-nowrap">{e.by.name}</td>
                <td className="px-3 py-2.5">
                  {e.kind && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-tint text-ink">{e.kind}</span>}
                  {e.reasons.length > 0 && <div className="text-xs text-copy mt-1">{e.reasons.join(', ')}</div>}
                </td>
                <td className="px-3 py-2.5 text-copy max-w-[260px]">
                  <div>{e.page.label}{e.about ? ` · ${e.about}` : ''}</div>
                  <Link to={e.page.path} className="text-xs text-teal-600 hover:underline break-all">{e.page.path}</Link>
                </td>
                <td className="px-3 py-2.5 text-ink whitespace-pre-wrap max-w-[420px]">{e.note || '—'}</td>
                <td className="px-3 py-2.5 text-muted whitespace-nowrap">{formatFeedbackTime(e.at)}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-muted text-sm">No feedback yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
